/**
 * O Inicio do cliente (spec 011, decisao 6; spec 038, secao 3): o destaque do plano atual, sem repetir o cartao da
 * casca (nome, etapa e subconta ja estao nele). Aqui fica o que ele nao mostra: o proximo passo com o botao que
 * resolve, o prazo do teste com a contagem, a proxima mensalidade, onde parou na Academy e as ultimas notificacoes.
 * Nome, CPF e e-mail moram no Perfil. Tudo vem pronto do backend; a tela so escolhe a cor e o lugar.
 */
import { Aviso, BarraDeProgresso, BotaoLink, Cartao, EsqueletoDeCartao, Indicador, LinkInterno, ListaDeNotificacoes, Pilha, ProximoPasso, Vazio, formatarDia, type Notificacao } from "@mtc/ui";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useNavigate } from "react-router";
import { useApi } from "../acoes";
import { nomeDaCobranca, valorDaParcela } from "../cobranca";
import { usePassoDoPlano } from "../passo";
import { usePlanoAtual } from "../planoAtual";
import { useSessao } from "../sessao";
import { useVisao } from "../visao";
import type { ListaDeAvisos, Matricula } from "../tipos";
import { useAcademy } from "./Academy";
import { useSolicitacoes } from "./Planos";

/** O prazo do teste pede atencao com 7 dias ou menos (spec 038, secao 3). */
const DIAS_DE_ATENCAO = 7;

export function PaginaInicio() {
  const { estado } = useSessao();
  const { atual, carregando } = usePlanoAtual();
  const [aviso, setAviso] = useState<string>();
  // Atalhos de partes que o administrador desligou nao aparecem (spec 060).
  const { mostra } = useVisao();
  if (estado.status !== "logado") return null;
  return (
    <Pilha espaco={6}>
      <header className="mtc-cabecalho__textos">
        <h1 className="mtc-titulo mtc-cabecalho__titulo">Olá, {estado.usuario.nome.split(" ")[0]}</h1>
      </header>
      {aviso && <Aviso tipo="sucesso">{aviso}</Aviso>}
      {carregando ? (
        <div className="mtc-inicio">
          <div className="mtc-inicio__coluna">
            <EsqueletoDeCartao linhas={2} barra rotulo="Buscando seu plano" />
            <div className="mtc-inicio__numeros"><EsqueletoDeCartao linhas={1} /><EsqueletoDeCartao linhas={1} /></div>
          </div>
          <div className="mtc-inicio__coluna"><EsqueletoDeCartao linhas={2} barra /><EsqueletoDeCartao linhas={3} /></div>
        </div>
      ) : (
        <div className="mtc-inicio">
          <div className="mtc-inicio__coluna">
            {atual ? <DestaqueDoPlano m={atual} aoConcluir={setAviso} /> : <Vazio titulo="Você ainda não tem um plano" acao={mostra("comprar") ? <BotaoLink href="/comprar" variante="primario">Ver planos à venda</BotaoLink> : undefined}>Quando você comprar um plano, o próximo passo dele aparece aqui.</Vazio>}
          </div>
          <div className="mtc-inicio__coluna">
            {estado.usuario.capacidades.modulos.includes("mercado") && mostra("mercado") && <AtalhoDoMercado />}
            {mostra("academy") && <ContinueNaAcademy />}
            <UltimasNotificacoes />
          </div>
        </div>
      )}
    </Pilha>
  );
}

/** O proximo passo (com o botao), e embaixo os numeros do plano: prazo do teste e proxima mensalidade. */
function DestaqueDoPlano({ m, aoConcluir }: { m: Matricula; aoConcluir: (aviso: string) => void }) {
  const solicitacoes = useSolicitacoes();
  const { passo, acoes, dialogos } = usePassoDoPlano(m, solicitacoes.data?.itens ?? [], aoConcluir);
  const prazo = prazoDoTeste(m);
  const mensalidade = proximaMensalidade(m, useVisao().mostra("mensalidades"));
  return (
    <>
      {solicitacoes.isLoading ? (
        <EsqueletoDeCartao linhas={2} barra rotulo="Buscando o próximo passo" />
      ) : (
        <ProximoPasso destaque passo={passo ?? { titulo: "Tudo em dia", texto: "Nada pendente no seu plano agora. Quando houver, aparece aqui.", tom: "sucesso" }} acoes={acoes} />
      )}
      {(prazo || mensalidade) && <div className="mtc-inicio__numeros">{prazo}{mensalidade}</div>}
      {dialogos}
    </>
  );
}

/** Prazo do teste, so no Periodo de teste: a contagem de dias que o backend mandou, em alerta com 7 ou menos. */
function prazoDoTeste(m: Matricula) {
  const e = m.modulos.etapas;
  if (!e || e.etapa !== "TESTE" || m.status === "ENCERRADA") return null;
  if (e.teste_vencido) return <Indicador key="prazo" rotulo="Prazo do teste" valor="Encerrado" detalhe={e.teste_ate ? `O prazo acabou em ${formatarDia(e.teste_ate)}. Seu plano aguarda a decisão da mesa.` : "Seu plano aguarda a decisão da mesa."} tom="erro" />;
  if (e.teste_sem_prazo || !e.teste_ate || e.dias_restantes_do_teste == null) return <Indicador key="prazo" rotulo="Prazo do teste" valor="Sem prazo" detalhe={e.prazo_motivo ?? undefined} />;
  const dias = e.dias_restantes_do_teste;
  return <Indicador key="prazo" rotulo="Prazo do teste" valor={dias} unidade={dias === 1 ? "dia restante" : "dias restantes"} detalhe={`Até ${formatarDia(e.teste_ate)}`} tom={dias <= DIAS_DE_ATENCAO ? "alerta" : "neutro"} />;
}

/** A proxima mensalidade: valor, vencimento e a situacao do backend (vencida em erro, vencendo em alerta). */
/** `comPagar`: o "Ver e pagar" leva pra Mensalidades, que o administrador pode ter desligado (spec 060). */
function proximaMensalidade(m: Matricula, comPagar: boolean) {
  const r = m.modulos.mensalidade;
  if (!r || m.status === "ENCERRADA") return null;
  // "Próxima mensalidade" ou, com o nome que a API mandou, "Próxima taxa de manutenção" (spec 064).
  const rotulo = `Próxima ${nomeDaCobranca(r.rotulo).toLowerCase()}`;
  if (!r.proxima) return <Indicador key="mensalidade" rotulo={rotulo} valor={r.situacao_rotulo} detalhe={r.sem_cobranca_motivo ?? undefined} />;
  const p = r.proxima;
  const tom = r.situacao === "VENCIDA" || r.situacao === "SUSPENSA" ? "erro" : r.situacao === "VENCE_HOJE" || r.situacao === "VENCE_EM_BREVE" ? "alerta" : "neutro";
  return (
    <Indicador key="mensalidade" rotulo={rotulo} valor={valorDaParcela(p)} detalhe={`Vence em ${p.vencimento_formatado}. ${r.situacao_rotulo}.`} tom={tom}>
      {p.pode_pagar && comPagar && <div><BotaoLink href="/mensalidades" tamanho="pequeno" variante={tom === "neutro" ? "secundario" : "primario"}>Ver e pagar</BotaoLink></div>}
    </Indicador>
  );
}

/** "Continue de onde parou" da Academy: a aula, o curso e o modulo, quanto ja viu e o botao. */
/** Mercado (spec 056): no celular ele nao cabe na barra de baixo, entao o caminho e este cartao (no desktop ajuda tambem). */
function AtalhoDoMercado() {
  return (
    <Cartao>
      <Pilha espaco={3}>
        <span className="mtc-rotulo">Mercado</span>
        <strong style={{ fontSize: "var(--t-md)", fontWeight: 500 }}>Cotações do dia e calendário econômico</strong>
        <div><BotaoLink href="/mercado" tamanho="pequeno">Abrir o Mercado</BotaoLink></div>
      </Pilha>
    </Cartao>
  );
}

function ContinueNaAcademy() {
  const q = useAcademy();
  if (q.isLoading) return <EsqueletoDeCartao linhas={1} barra rotulo="Buscando a Academy" />;
  const d = q.data;
  if (!d || !d.acesso.pode) return null;
  if (!d.continuar) {
    if (!d.cursos.length) return null;
    return (
      <Cartao>
        <Pilha espaco={3}>
          <span className="mtc-rotulo">Academy</span>
          <strong style={{ fontSize: "var(--t-md)", fontWeight: 500 }}>Comece pelos cursos da Academy</strong>
          <div><BotaoLink href="/academy" tamanho="pequeno">Abrir a Academy</BotaoLink></div>
        </Pilha>
      </Cartao>
    );
  }
  const c = d.continuar;
  const aula = d.cursos.flatMap((x) => x.modulos.flatMap((mo) => mo.aulas)).find((a) => a.id === c.aula_id);
  return (
    <Cartao>
      <Pilha espaco={3}>
        <span className="mtc-rotulo">Continue de onde parou</span>
        <strong style={{ fontSize: "var(--t-lg)", fontWeight: 400, overflowWrap: "anywhere" }}>{c.titulo}</strong>
        <span className="mtc-texto-2" style={{ fontSize: "var(--t-sm)" }}>{c.curso}, {c.modulo}</span>
        {aula && <BarraDeProgresso percentual={aula.progresso.concluida ? 100 : aula.progresso.percentual} rotulo={`Quanto você viu de ${c.titulo}`} mostrarNumero />}
        <div><BotaoLink href={`/academy/aulas/${c.aula_id}`} variante="primario">Continuar</BotaoLink></div>
      </Pilha>
    </Cartao>
  );
}

/** As 3 ultimas notificacoes, com "Ver todas". Abrir marca como lida e leva ao link. */
function UltimasNotificacoes() {
  const { ler, postar } = useApi();
  const qc = useQueryClient();
  const navegar = useNavigate();
  const q = useQuery({ queryKey: ["avisos", "inicio"], queryFn: () => ler<ListaDeAvisos>("/v1/me/notificacoes?limite=3") });
  const abrir = (n: Notificacao) => {
    if (!n.lida) void postar(`/v1/me/notificacoes/${n.id}/lida`).then(() => qc.invalidateQueries({ queryKey: ["avisos"] }), () => undefined);
    if (n.link?.startsWith("/")) navegar(n.link);
  };
  if (q.isLoading) return <EsqueletoDeCartao linhas={3} rotulo="Buscando suas notificações" />;
  if (q.isError) return null;
  const itens = q.data?.itens ?? [];
  return (
    <Cartao>
      <Pilha espaco={3}>
        <div className="mtc-linha mtc-linha--entre" style={{ gap: "var(--e-2)" }}>
          <span className="mtc-rotulo">Últimas notificações</span>
          <LinkInterno href="/notificacoes">Ver todas</LinkInterno>
        </div>
        {itens.length ? (
          <div style={{ margin: "0 calc(-1 * var(--e-3))" }}><ListaDeNotificacoes itens={itens} aoAbrir={abrir} rotulo="Últimas notificações" /></div>
        ) : (
          <p className="mtc-texto-2" style={{ margin: 0, fontSize: "var(--t-sm)" }}>Nenhuma notificação por enquanto.</p>
        )}
      </Pilha>
    </Cartao>
  );
}

export function PaginaEmConstrucao({ titulo }: { titulo: string }) {
  return (
    <div style={{ display: "grid", gap: "var(--e-4)" }}>
      <h1 className="mtc-titulo mtc-cabecalho__titulo">{titulo}</h1>
      <Cartao><p className="mtc-texto-2" style={{ margin: 0 }}>Esta tela ainda está em construção.</p></Cartao>
    </div>
  );
}
